import { useEffect, useState } from "react";
import { store } from "./store/store";
import { getRuntimeApiUrl } from "./runtimeApiUrl";

// Module-level cache so the same image isn't re-fetched when a component
// remounts (e.g. when the MatchFillWidget unmounts and the Match page
// mounts the same profile photo — the hero blob is already in hand and
// we can skip the loading flash entirely).
const blobCache = new Map<string, Blob>();
const inflight = new Map<string, Promise<Blob | null>>();

const downloadMediaBlob = async (path: string): Promise<Blob | null> => {
  const cached = blobCache.get(path);
  if (cached) return cached;

  const pending = inflight.get(path);
  if (pending) return pending;

  const url = new URL(`/media${path}`, getRuntimeApiUrl());
  const promise = fetch(url, {
    headers: { Authorization: `Bearer ${store.data.auth.token}` },
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const blob = await response.blob();
      blobCache.set(path, blob);
      return blob;
    })
    .finally(() => {
      inflight.delete(path);
    });
  inflight.set(path, promise);
  return promise;
};

interface MediaBlobState {
  path: string;
  loading: boolean;
  blob: Blob | null;
  /**
   * True when `blob` came straight out of the module cache with no network
   * round trip. Consumers can skip fade-in styling for these — a cached
   * image was almost certainly just on screen (e.g. the swipe deck's
   * behind-card layer) and fading it back in reads as a flash.
   */
  fromCache: boolean;
  error?: string;
}

export const useMediaBlob = (path: string) => {
  const cached = path ? blobCache.get(path) ?? null : null;
  const [state, setState] = useState<MediaBlobState>({
    path,
    loading: !!path && !cached,
    blob: cached,
    fromCache: !!cached,
  });
  // Pick a cache hit up synchronously in the render where `path` changes
  // (e.g. MediaBlobImage resolving its measured variant during the mount
  // commit). Deferring to the effect below lets the browser paint a
  // placeholder frame first — visible as a white flash when a fresh swipe
  // card mounts over the identical, already-loaded photo.
  if (state.path !== path) {
    setState({
      path,
      loading: !!path && !cached,
      blob: cached,
      fromCache: !!cached,
    });
  }
  useEffect(() => {
    if (!path) return;
    // Covers a blob cached by another consumer after our render-time check.
    const existing = blobCache.get(path);
    if (existing) {
      setState((prev) =>
        prev.blob === existing && !prev.loading
          ? prev
          : { ...prev, loading: false, blob: existing, fromCache: true }
      );
      return;
    }
    let cancelled = false;
    downloadMediaBlob(path)
      .then((blob) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: undefined,
          blob,
          fromCache: false,
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.toString(),
          blob: null,
          fromCache: false,
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return state;
};
